import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Plus, AlertTriangle, ShieldCheck, FileText, Filter } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { usePersistedState } from '../../lib/persist.js'
import { fullName } from '../../lib/employee.js'
import { nameInitial } from '../../lib/text.js'
import { EmployeeTrigger } from '../../components/employee/EmployeeDrawer.jsx'
import { warningLabel, warningTone, warningState, fmtDay, fmtStamp, needsAck } from '../../lib/warning.js'
import EmployeePicker from '../../components/employee/EmployeePicker.jsx'
import { useEmployeeFilter } from '../../components/employee/EmployeeFilter.jsx'
import WarningEditor from '../../components/employee/WarningEditor.jsx'

/**
 * พนักงาน › เอกสาร › หนังสือเตือน — the register, and where one is issued.
 *
 * 🚨 A FLAT LIST BY THE OWNER'S DECISION. No ครั้งที่ 1/2/3, no one-year expiry, and
 * therefore NO SCREEN HERE MAY IMPLY A STRIKE COUNT. There is no red counter beside
 * a name and no "2 active warnings" chip, because the clinic has not defined what a
 * count would mean and a number on a personnel screen reads as a verdict. Rows are
 * grouped by person so the history is visible; that is as far as it goes.
 *
 * 🚨 And there is no printed letter (also the owner's decision), so the record's
 * whole evidentiary weight is the รับทราบ column — a timestamp written by the
 * subject's own authenticated session — plus any scan filed against it. That is
 * why รับทราบ is a column and not a detail hidden in the drawer.
 *
 * Issued from HERE rather than from inside EmployeeEditor.jsx: that file is one
 * 806-line form with no tab structure, and a warnings section would make it 950
 * lines of unrelated concerns.
 */
export default function EmployeeWarning() {
  const [rows, setRows] = useState([])
  const [employees, setEmployees] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  // เอกสาร's own person, shared by its three tabs only — see EmployeeFilter.jsx.
  const [employeeId, setEmployeeId] = useEmployeeFilter('docs')
  const [onlyPending, setOnlyPending] = usePersistedState('emp:warn:pending', false)
  const [editing, setEditing] = useState(null)   // row | 'new'

  // ⚠️ Fetches EVERYONE, including inactive — do not narrow this to `active: true`.
  // The register has to name the subject of a warning issued to someone who has since
  // left, and an active-only roster would render those rows as "#417". Only the
  // PICKER is filtered to active (EmployeePicker); the lookup map needs the lot.
  // (`per_page: 500` with no other params also shares cachedGet's entry with the
  // other HR pages, so this costs no extra request.)
  useEffect(() => {
    cachedGet('/api/hr/employees', { per_page: 500 })
      .then((r) => setEmployees(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const r = await apiGet('/api/hr/warning', employeeId ? { employee_id: employeeId } : undefined)
      setRows(Array.isArray(r?.items) ? r.items : [])
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดหนังสือเตือนไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [employeeId])

  useEffect(() => { load() }, [load])

  const empById = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(Number(e.id), e)
    return m
  }, [employees])

  const visible = useMemo(
    () => (onlyPending ? rows.filter(needsAck) : rows),
    [rows, onlyPending],
  )

  const awaiting = rows.filter(needsAck).length

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-2 min-w-0">
          <Filter size={13} className="text-slate-400 shrink-0" />
          <EmployeePicker
            value={employeeId}
            onChange={setEmployeeId}
            allowAll
            allLabel="พนักงานทุกคน"
            className="w-[280px]"
          />
          {employeeId && (
            <button
              type="button"
              onClick={() => setEmployeeId('')}
              className="text-[12px] text-slate-400 hover:text-slate-700 shrink-0"
            >
              ล้าง
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOnlyPending((v) => !v)}
          className={`px-2.5 py-1.5 rounded-lg text-[13px] transition ${
            onlyPending ? 'bg-amber-50 text-amber-700 ring-1 ring-amber-200 font-medium' : 'text-slate-500 hover:bg-slate-50'
          }`}
        >
          รอรับทราบ{awaiting > 0 && <span className="ml-1 tabular-nums">({awaiting})</span>}
        </button>
        <button
          type="button"
          onClick={() => setEditing('new')}
          className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700"
        >
          <Plus size={14} /> ออกหนังสือเตือน
        </button>
      </div>

      {err && <p className="text-[13px] text-rose-600 mb-3">{err}</p>}

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : visible.length === 0 ? (
        // A failed load is not "nothing here" — the error above already says so.
        err ? null : <div className="py-16 text-center">
          <ShieldCheck size={24} className="mx-auto text-emerald-300 mb-2" />
          <p className="text-[13.5px] text-slate-400">
            {onlyPending ? 'ไม่มีหนังสือเตือนที่รอการรับทราบ' : 'ยังไม่มีหนังสือเตือน'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[11px] text-slate-400 bg-slate-50/70 text-left">
                <th className="px-2.5 py-1.5 font-medium">พนักงาน</th>
                <th className="px-2.5 py-1.5 font-medium">เรื่อง</th>
                <th className="px-2.5 py-1.5 font-medium">วันที่ออก</th>
                <th className="px-2.5 py-1.5 font-medium">การรับทราบ</th>
                <th className="px-2.5 py-1.5 font-medium">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((w) => {
                const emp = empById.get(Number(w.employee_id))
                const voided = warningState(w) === 'voided'
                return (
                  <tr
                    key={w.id}
                    onClick={() => setEditing(w)}
                    className="border-t border-slate-50 hover:bg-brand-50/30 cursor-pointer"
                  >
                    {/* The row opens the WARNING; the person cell opens the person. */}
                    <td className="px-2.5 py-2">
                      <EmployeeTrigger employeeId={w.employee_id} className="items-center gap-2">
                        <span className="w-7 h-7 rounded-lg bg-slate-100 text-slate-500 text-[11.5px] font-semibold flex items-center justify-center shrink-0 ring-1 ring-slate-200">
                          {nameInitial(emp?.nickname || emp?.name || '?')}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 truncate">{emp?.code || `#${w.employee_id}`}</div>
                          <div className="text-[11px] text-slate-400 truncate">{emp ? fullName(emp) : ''}</div>
                        </div>
                      </EmployeeTrigger>
                    </td>
                    <td className="px-2.5 py-2 max-w-[280px]">
                      <div className={`truncate font-medium ${voided ? 'text-slate-400 line-through' : 'text-slate-800'}`}>
                        {w.subject}
                      </div>
                      {Array.isArray(w.file) && w.file.length > 0 && (
                        <div className="flex items-center gap-1 text-[11px] text-slate-400 mt-0.5">
                          <FileText size={10} /> {w.file.length} ไฟล์
                        </div>
                      )}
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap tabular-nums text-slate-600">
                      {fmtDay(w.issue_date || w.created_at)}
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap">
                      {w.acknowledged_at
                        ? <span className="text-emerald-700 tabular-nums">{fmtStamp(w.acknowledged_at)}</span>
                        : voided
                          ? <span className="text-slate-400">—</span>
                          : <span className="text-amber-700">ยังไม่รับทราบ</span>}
                    </td>
                    <td className="px-2.5 py-2">
                      <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 whitespace-nowrap ${warningTone(w)}`}>
                        {warningLabel(w)}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <p className="mt-3 flex items-start gap-1.5 text-[11.5px] text-slate-400">
        <AlertTriangle size={12} className="shrink-0 mt-0.5" />
        ระบบเก็บเป็นบันทึกภายใน ไม่ได้ออกเป็นเอกสารพิมพ์ — หลักฐานการแจ้งคือวันเวลาที่พนักงานกดรับทราบ
        และไฟล์แนบ (เช่น สแกนหนังสือที่ลงนามแล้ว)
      </p>

      {editing && (
        <WarningEditor
          warning={editing === 'new' ? null : editing}
          employees={employees}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}
    </div>
  )
}
